/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Sparkles, Check, X, Eye, EyeOff, Star, Clock, ShieldCheck, AlertCircle,
  Calendar, RefreshCcw, Pencil, Trash2, Save,
} from "lucide-react";
import API from "../lib/api";
import { useAuth } from "../lib/auth";
import { Section } from "../components/Common";
import { errorMessage } from "../lib/errors";

/**
 * Phase 6.4 — Admin Offers Dashboard.
 *
 * Visible to admins only (is_admin === true on /api/auth/me).
 * Tabs by status; per-row actions: approve, reject, feature, hide, expire, edit, delete.
 * Inline edit form for any pending or approved offer.
 */

const STATUS_TAB_KEYS = ["pending", "approved", "rejected", "hidden", "expired", "all"];

export default function AdminOffersPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // offer_id | null
  const [editForm, setEditForm] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const STATUS_TABS = STATUS_TAB_KEYS.map((k) => ({ key: k, label: t(`admin.tabs.${k}`) }));

  // ----- gate
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/admin/offers")}`);
      return;
    }
    if (!user.is_admin) {
      toast.error(t("admin.not_admin", "Admin access required"));
      navigate("/");
    }
  }, [user, authLoading, navigate, t]);

  // ----- load
  const load = async (s = status) => {
    if (!user?.is_admin) return;
    setLoading(true);
    try {
      const params = s && s !== "all" ? `?status=${s}&limit=200` : "?limit=200";
      const { data } = await API.get(`/admin/offers${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(errorMessage(e, t("admin.load_failed", "Couldn't load offers")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, status]);

  // ----- actions
  const approve = async (id) => {
    try {
      await API.patch(`/admin/offers/${id}/approve`);
      toast.success(t("admin.approved", "Offer approved"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const reject = async (id) => {
    try {
      await API.patch(`/admin/offers/${id}/reject`, { reason: rejectReason });
      toast.success(t("admin.rejected", "Offer rejected"));
      setRejectingId(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const feature = async (id, value) => {
    try {
      await API.patch(`/admin/offers/${id}/feature`, { featured: value });
      toast.success(value ? t("admin.featured", "Offer featured") : t("admin.unfeatured", "Removed from featured"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const hide = async (id) => {
    try {
      await API.patch(`/admin/offers/${id}/hide`);
      toast.success(t("admin.hidden", "Offer hidden"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const expire = async (id) => {
    try {
      await API.patch(`/admin/offers/${id}/expire`);
      toast.success(t("admin.expired", "Offer expired"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("admin.confirm_delete", "Delete this offer permanently?"))) return;
    try {
      await API.delete(`/business/offers/${id}`);
      toast.success(t("admin.deleted", "Offer deleted"));
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const startEdit = (offer) => {
    setEditing(offer.offer_id);
    setEditForm({
      title: offer.title || "",
      description: offer.description || "",
      discount_label: offer.discount_label || "",
      promo_code: offer.promo_code || "",
      ends_at: offer.ends_at ? offer.ends_at.slice(0, 10) : "",
      terms: offer.terms || "",
    });
  };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async (id) => {
    try {
      const payload = { ...editForm };
      if (payload.ends_at) payload.ends_at = new Date(payload.ends_at).toISOString();
      else payload.ends_at = null;
      await API.patch(`/business/offers/${id}`, payload);
      toast.success(t("admin.saved", "Saved"));
      cancelEdit();
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const tabCounts = useMemo(() => ({}), []); // placeholder; counts could come from API later

  if (authLoading || (user && !user.is_admin)) {
    return (
      <Section eyebrow="— Admin" title={t("admin.loading", "Loading…")} />
    );
  }
  if (!user) return null; // redirected

  return (
    <div data-testid="page-admin-offers">
      {/* Header */}
      <section className="border-b border-white/10 bg-gradient-to-r from-[#0E0F14] to-[#080910]">
        <div className="app-container py-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center">
              <ShieldCheck className="w-4 h-4 gold-text" />
            </div>
            <div className="eyebrow !mb-0">{t("admin.eyebrow", "— Admin Console")}</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="admin-title">
            {t("admin.offers_title", "Business Offers")}
          </h1>
          <p className="text-white/70 mt-3 max-w-[60ch]">
            {t(
              "admin.offers_subtitle",
              "Approve, feature, hide, or expire offers proposed by partner businesses."
            )}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs text-white/55">
            <Sparkles className="w-3 h-3 gold-text" /> {t("admin.signed_in_as")} <span className="text-white/80">{user.email}</span>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <Section eyebrow="" title="">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatus(tab.key)}
              data-testid={`admin-tab-${tab.key}`}
              className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${
                status === tab.key
                  ? "bg-gradient-to-r from-[#E8D199] via-[#D4AF37] to-[#7A5F2A] border-transparent text-black"
                  : "border-white/15 text-white/75 hover:border-[#C8A24A]/45 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
          <button
            onClick={() => load()}
            className="text-xs px-3.5 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-[#C8A24A]/45 inline-flex items-center gap-1.5"
            data-testid="admin-refresh"
          >
            <RefreshCcw className="w-3 h-3" /> {t("admin.refresh", "Refresh")}
          </button>
        </div>

        {loading ? (
          <div className="card-surface p-10 text-center text-white/60">{t("admin.loading", "Loading…")}</div>
        ) : items.length === 0 ? (
          <div className="card-surface p-10 text-center text-white/60" data-testid="admin-empty">
            {t("admin.no_items", "No offers in this view.")}
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-offers-list">
            {items.map((o) => (
              <div
                key={o.offer_id}
                className="card-surface p-4 md:p-5"
                data-testid={`admin-offer-${o.offer_id}`}
              >
                {editing === o.offer_id ? (
                  <EditForm
                    form={editForm}
                    setForm={setEditForm}
                    onCancel={cancelEdit}
                    onSave={() => saveEdit(o.offer_id)}
                  />
                ) : (
                  <Row
                    offer={o}
                    onApprove={() => approve(o.offer_id)}
                    onReject={() => { setRejectingId(o.offer_id); setRejectReason(""); }}
                    onFeature={() => feature(o.offer_id, !o.featured)}
                    onHide={() => hide(o.offer_id)}
                    onExpire={() => expire(o.offer_id)}
                    onEdit={() => startEdit(o)}
                    onDelete={() => del(o.offer_id)}
                  />
                )}

                {/* Inline reject reason */}
                {rejectingId === o.offer_id && (
                  <div className="mt-3 border-t border-white/10 pt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      placeholder={t("admin.reject_reason_placeholder", "Reason (optional, shown to the owner)")}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="flex-1 bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
                      data-testid={`admin-reject-reason-${o.offer_id}`}
                    />
                    <button
                      onClick={() => reject(o.offer_id)}
                      className="btn-primary text-xs"
                      data-testid={`admin-confirm-reject-${o.offer_id}`}
                    >
                      {t("admin.confirm_reject", "Confirm reject")}
                    </button>
                    <button
                      onClick={() => { setRejectingId(null); setRejectReason(""); }}
                      className="btn-ghost text-xs"
                    >
                      {t("admin.cancel", "Cancel")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Row({ offer, onApprove, onReject, onFeature, onHide, onExpire, onEdit, onDelete }) {
  const { t } = useTranslation();
  const biz = offer.business || {};
  const statusColor = {
    pending: "bg-amber-500/20 text-amber-200 border-amber-500/30",
    approved: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30",
    rejected: "bg-rose-500/20 text-rose-200 border-rose-500/30",
    hidden: "bg-white/10 text-white/70 border-white/15",
    expired: "bg-white/5 text-white/55 border-white/10",
  }[offer.status] || "bg-white/10 text-white/70 border-white/15";

  return (
    <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full border ${statusColor}`}>
            {t(`admin.statuses.${offer.status}`, offer.status)}
          </span>
          {offer.featured && (
            <span className="text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full border border-[#C8A24A]/45 gold-text inline-flex items-center gap-1">
              <Star className="w-2.5 h-2.5" /> {t("admin.featured_label")}
            </span>
          )}
          <span className="text-[11px] text-white/55">
            {t("admin.biz_label")}: <span className="text-white/80">{biz.name || offer.business_id}</span>
          </span>
        </div>
        <div className="font-display text-lg leading-tight">{offer.title}</div>
        <div className="text-xs gold-text mt-0.5">{offer.discount_label}</div>
        <p className="text-sm text-white/72 leading-relaxed mt-1.5 line-clamp-2">{offer.description}</p>
        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-white/55">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {offer.ends_at ? t("admin.until", { date: new Date(offer.ends_at).toLocaleDateString() }) : t("admin.no_end")}
          </span>
          {offer.promo_code && (
            <span className="inline-flex items-center gap-1 font-mono">{t("admin.code_label")}: <span className="text-white/85">{offer.promo_code}</span></span>
          )}
          <span className="inline-flex items-center gap-1">{t("admin.reveals_label")}: <span className="text-white/85">{offer.reveal_count || 0}</span></span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 lg:justify-end">
        {offer.status === "pending" && (
          <>
            <button onClick={onApprove} className="action-btn-pos" data-testid={`admin-approve-${offer.offer_id}`}>
              <Check className="w-3.5 h-3.5" /> {t("admin.approve", "Approve")}
            </button>
            <button onClick={onReject} className="action-btn-neg" data-testid={`admin-reject-${offer.offer_id}`}>
              <X className="w-3.5 h-3.5" /> {t("admin.reject", "Reject")}
            </button>
          </>
        )}
        {offer.status === "approved" && (
          <>
            <button onClick={onFeature} className="action-btn" data-testid={`admin-feature-${offer.offer_id}`}>
              <Star className="w-3.5 h-3.5" /> {offer.featured ? t("admin.unfeature", "Unfeature") : t("admin.feature", "Feature")}
            </button>
            <button onClick={onHide} className="action-btn" data-testid={`admin-hide-${offer.offer_id}`}>
              <EyeOff className="w-3.5 h-3.5" /> {t("admin.hide", "Hide")}
            </button>
            <button onClick={onExpire} className="action-btn" data-testid={`admin-expire-${offer.offer_id}`}>
              <Clock className="w-3.5 h-3.5" /> {t("admin.expire", "Expire")}
            </button>
          </>
        )}
        {(offer.status === "hidden" || offer.status === "expired" || offer.status === "rejected") && (
          <button onClick={onApprove} className="action-btn-pos" data-testid={`admin-reapprove-${offer.offer_id}`}>
            <Check className="w-3.5 h-3.5" /> {t("admin.reapprove", "Re-approve")}
          </button>
        )}
        <button onClick={onEdit} className="action-btn" data-testid={`admin-edit-${offer.offer_id}`}>
          <Pencil className="w-3.5 h-3.5" /> {t("admin.edit", "Edit")}
        </button>
        <button onClick={onDelete} className="action-btn-neg" data-testid={`admin-delete-${offer.offer_id}`}>
          <Trash2 className="w-3.5 h-3.5" /> {t("admin.delete", "Delete")}
        </button>
        {/* helper styles via inline class definitions below */}
        <style>{`
          .action-btn { display:inline-flex; align-items:center; gap:0.35rem; font-size:0.75rem; padding:0.45rem 0.75rem; border-radius:0.5rem; border:1px solid rgba(255,255,255,0.12); color:rgba(255,255,255,0.85); background:rgba(255,255,255,0.025); transition:all 0.2s ease; }
          .action-btn:hover { border-color: rgba(200,162,74,0.45); color:#fff; }
          .action-btn-pos { display:inline-flex; align-items:center; gap:0.35rem; font-size:0.75rem; padding:0.45rem 0.75rem; border-radius:0.5rem; border:1px solid rgba(16,185,129,0.4); color:#a7f3d0; background:rgba(16,185,129,0.12); transition:all 0.2s ease; }
          .action-btn-pos:hover { background:rgba(16,185,129,0.2); color:#fff; }
          .action-btn-neg { display:inline-flex; align-items:center; gap:0.35rem; font-size:0.75rem; padding:0.45rem 0.75rem; border-radius:0.5rem; border:1px solid rgba(244,63,94,0.35); color:#fda4af; background:rgba(244,63,94,0.10); transition:all 0.2s ease; }
          .action-btn-neg:hover { background:rgba(244,63,94,0.2); color:#fff; }
        `}</style>
      </div>
    </div>
  );
}

function EditForm({ form, setForm, onCancel, onSave }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-2.5" data-testid="admin-edit-form">
      <input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder={t("admin.edit_title_ph", "Title")}
        className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
      />
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder={t("admin.edit_desc_ph", "Description")}
        rows={3}
        className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
      />
      <div className="grid sm:grid-cols-3 gap-2">
        <input
          value={form.discount_label}
          onChange={(e) => setForm({ ...form, discount_label: e.target.value })}
          placeholder={t("admin.edit_discount_ph", "Discount label (e.g. 20% OFF)")}
          className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <input
          value={form.promo_code}
          onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
          placeholder={t("admin.edit_code_ph", "Promo code")}
          className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
        />
        <input
          type="date"
          value={form.ends_at}
          onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
          className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <textarea
        value={form.terms}
        onChange={(e) => setForm({ ...form, terms: e.target.value })}
        placeholder={t("admin.edit_terms_ph", "Terms & conditions")}
        rows={2}
        className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
      />
      <div className="flex gap-2 mt-1">
        <button onClick={onSave} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="admin-save-edit">
          <Save className="w-3.5 h-3.5" /> {t("admin.save", "Save")}
        </button>
        <button onClick={onCancel} className="btn-ghost text-xs">
          {t("admin.cancel", "Cancel")}
        </button>
      </div>
    </div>
  );
}
